import type {
  GetUserNetworksForContextMenuQuery,
  MyCirclesQuery,
} from '@/graphql/api/generated'

type CircleEdge = NonNullable<
  NonNullable<MyCirclesQuery['myCircles']>['edges']
>[number]
type NetworkEdge = NonNullable<
  NonNullable<GetUserNetworksForContextMenuQuery['myNetworks']>['edges']
>[number]

export type CircleNode = NonNullable<CircleEdge['node']>
export type NetworkNode = NonNullable<NetworkEdge['node']>

type NetworkCircleNode = NonNullable<
  NonNullable<NonNullable<NetworkNode['circles']>['edges']>[number]['node']
>

type CircleTreeNode = {
  type?: 'network' | 'circle'
  slug?: string
  children?: unknown[]
}

export const getFilteredCircles = ({
  circles,
  shouldEnableCircleFilter,
  debouncedSearchTerm,
  matchedCircleIds,
}: {
  circles: CircleNode[]
  shouldEnableCircleFilter: boolean
  debouncedSearchTerm: string
  matchedCircleIds: Set<string>
}): CircleNode[] => {
  if (!shouldEnableCircleFilter) return circles
  if (!debouncedSearchTerm) return circles

  const normalizedSearch = debouncedSearchTerm.toLocaleLowerCase()
  const fallbackFilteredCircles = circles.filter((circle) =>
    circle.name.toLocaleLowerCase().includes(normalizedSearch)
  )

  if (matchedCircleIds.size === 0) return fallbackFilteredCircles

  return circles.filter((circle) => matchedCircleIds.has(circle.id))
}

/** Circles to show outside network blocks (root-level tree nodes, orphans, stale-cache cases). */
export const getCirclesNotGroupedUnderNetworks = ({
  filteredCircles,
  groupedNetworkCircles,
}: {
  filteredCircles: CircleNode[]
  groupedNetworkCircles: Array<{ circles: CircleNode[] }>
}): CircleNode[] => {
  const groupedIds = new Set<string>()
  groupedNetworkCircles.forEach((group) => {
    group.circles.forEach((circle) => {
      groupedIds.add(circle.id)
    })
  })
  return filteredCircles.filter((circle) => !groupedIds.has(circle.id))
}

const matchesSearch = (circle: CircleNode, debouncedSearchTerm: string) => {
  if (!debouncedSearchTerm) return true
  return circle.name
    .toLocaleLowerCase()
    .includes(debouncedSearchTerm.toLocaleLowerCase())
}

const collectCirclesFromNetworkEdges = ({
  network,
  circlesBySlug,
  debouncedSearchTerm,
}: {
  network: NetworkNode
  circlesBySlug: Map<string, CircleNode>
  debouncedSearchTerm: string
}): CircleNode[] => {
  return (network.circles?.edges ?? [])
    .map((edge) => edge?.node)
    .filter((networkCircle): networkCircle is NetworkCircleNode =>
      Boolean(networkCircle?.slug)
    )
    .map((networkCircle) => circlesBySlug.get(networkCircle.slug))
    .filter((circle): circle is CircleNode => Boolean(circle))
    .filter((circle) => matchesSearch(circle, debouncedSearchTerm))
}

export const getGroupedNetworkCircles = ({
  networks,
  circlesBySlug,
  rawTree,
  debouncedSearchTerm,
}: {
  networks: NetworkNode[]
  circlesBySlug: Map<string, CircleNode>
  rawTree: unknown
  debouncedSearchTerm: string
}): Array<{ network: NetworkNode; circles: CircleNode[] }> => {
  const searchTermNormalized = debouncedSearchTerm.toLocaleLowerCase()
  const networkBySlug = new Map(
    networks.map((network) => [network.slug, network])
  )
  const parsedTree = parseTree(rawTree)
  const groupedByTree: Array<{ network: NetworkNode; circles: CircleNode[] }> =
    []

  parsedTree.forEach((node) => {
    if (node?.type !== 'network' || !node.slug) return
    const network = networkBySlug.get(node.slug)
    if (!network) return

    const circles = flattenCircleNodes(node.children)
      .map((circleNode) => circleNode.slug)
      .filter((slug): slug is string => Boolean(slug))
      .map((slug) => circlesBySlug.get(slug))
      .filter((circle): circle is CircleNode => Boolean(circle))
      .filter((circle) => {
        if (!debouncedSearchTerm) return true
        return circle.name.toLocaleLowerCase().includes(searchTermNormalized)
      })

    groupedByTree.push({ network, circles })
  })

  const groupsFromNetworkEdgesOnly = () =>
    networks.map((network) => ({
      network,
      circles: collectCirclesFromNetworkEdges({
        network,
        circlesBySlug,
        debouncedSearchTerm,
      }),
    }))

  if (groupedByTree.length === 0) {
    return groupsFromNetworkEdgesOnly()
  }

  const seenSlugs = new Set(groupedByTree.map((g) => g.network.slug))
  const extraFromApi = networks
    .filter((network) => !seenSlugs.has(network.slug))
    .map((network) => ({
      network,
      circles: collectCirclesFromNetworkEdges({
        network,
        circlesBySlug,
        debouncedSearchTerm,
      }),
    }))

  return [...groupedByTree, ...extraFromApi]
}

export const buildCircleParentById = ({
  circles,
  rawTree,
}: {
  circles: CircleNode[]
  rawTree: unknown
}): Map<string, string> => {
  const parentById = new Map<string, string>()
  const parsedTree = parseTree(rawTree)

  const circlesBySlug = new Map(circles.map((circle) => [circle.slug, circle]))

  const walk = (nodes: CircleTreeNode[], parentId: string | null) => {
    nodes.forEach((node) => {
      if (node?.type === 'network') {
        const children = Array.isArray(node.children)
          ? (node.children as CircleTreeNode[])
          : []
        walk(children, null)
        return
      }
      if (!node?.slug) return
      const circle = circlesBySlug.get(node.slug)
      if (!circle) return
      if (parentId) {
        parentById.set(circle.id, parentId)
      }
      const children = Array.isArray(node.children)
        ? (node.children as CircleTreeNode[])
        : []
      walk(children, circle.id)
    })
  }

  walk(parsedTree, null)
  return parentById
}

export const getAuthorizedOrderBySlug = (
  rawTree: unknown
): Map<string, number> => {
  const parsedTree = parseTree(rawTree)
  const orderBySlug = new Map<string, number>()
  let order = 0

  const walk = (nodes: CircleTreeNode[]) => {
    nodes.forEach((node) => {
      if (node?.slug) {
        orderBySlug.set(node.slug, order)
        order += 1
      }
      if (Array.isArray(node?.children)) {
        walk(node.children as CircleTreeNode[])
      }
    })
  }

  walk(parsedTree)
  return orderBySlug
}

const parseTree = (rawTree: unknown): CircleTreeNode[] => {
  if (Array.isArray(rawTree)) {
    return rawTree as CircleTreeNode[]
  }
  if (typeof rawTree === 'string') {
    try {
      const parsed = JSON.parse(rawTree)
      if (Array.isArray(parsed)) {
        return parsed as CircleTreeNode[]
      }
    } catch {
      return []
    }
  }
  return []
}

const flattenCircleNodes = (rawNodes: unknown): CircleTreeNode[] => {
  const nodes = Array.isArray(rawNodes) ? (rawNodes as CircleTreeNode[]) : []
  const result: CircleTreeNode[] = []

  const walk = (currentNodes: CircleTreeNode[]) => {
    currentNodes.forEach((node) => {
      if (node?.type !== 'network' && node?.slug) {
        result.push(node)
      }
      if (Array.isArray(node?.children)) {
        walk(node.children as CircleTreeNode[])
      }
    })
  }

  walk(nodes)
  return result
}

export const getVisibleDepth = (
  circleId: string,
  visibleCircleIds: Set<string>,
  circleParentById: Map<string, string>
): number => {
  let depth = 0
  let currentParentId = circleParentById.get(circleId)

  while (currentParentId && visibleCircleIds.has(currentParentId)) {
    depth += 1
    currentParentId = circleParentById.get(currentParentId)
  }

  return depth
}
